import React from "react";

import DashboardLogo from "../../assets/img/dashboard-logo.png";
import PrintProgramSection from "./PrintProgramSection";
import {
	QUICK_PRICER_PRINT_SECTIONS,
	getFieldDisplayLabel,
	getFieldLabelText,
	formatGeneratedTimestamp,
} from "../../utils/quickPricerPrintHelpers";

import "./PrintDocument.scss";

const SUMMARY_TILE_FIELDS = [
	{ label: "Income Documentation Type", field: "incomeVerificationType" },
	{ label: "Loan Purpose", field: "loanPurpose" },
	{ label: "Occupancy", field: "occupancy" },
	{ label: "Qualifying FICO", field: "qualifyingCreditScore" },
];

const DISCLAIMER_TEXT =
	"Pricing is updated daily between 11:00 AM EST and 11:30 AM EST and may change at any time without prior notice. Quotes provided through this tool are estimates only. Definitive pricing is established solely through a completed and accepted Lock Request. Please log in to ensure accurate pricing.";

const PrintDocument = ({
	formState,
	selectedProducts,
	selectedRateKeys,
	currentUser,
	orgLabel,
	generatedAt,
	isCorrOrNdcChannel,
	isPublic,
}) => {
	return (
		<div className="qp-print-root">
			{/* Plain block, not <thead>: keeps this out of the table's print
			    pagination so it renders once on page 1 instead of repeating on
			    every page (the browser default for table thead/tfoot on print). */}
			<div className="qp-print-header">
				<div className="qp-print-brand">
					<img src={DashboardLogo} alt="LoganConnect" className="qp-print-logo" />
					<div>
						<div className="qp-print-brand-name">LoganConnect</div>
						<div className="qp-print-brand-subtitle">Quick Pricer Scenario</div>
					</div>
				</div>

				<div className="qp-print-header-meta">
					<div className="qp-print-header-title">Pricing Scenario Results</div>
					<div className="qp-print-header-detail">
						Generated: {formatGeneratedTimestamp(generatedAt)}
					</div>
					{currentUser?.email && (
						<div className="qp-print-header-detail">
							Run by: {currentUser.email}
						</div>
					)}
					{orgLabel && <div className="qp-print-header-detail">{orgLabel}</div>}
				</div>
			</div>

			<table className="qp-print-body-table">
				<tbody>
					<tr>
						<td>
							<div className="qp-print-summary-strip">
								{SUMMARY_TILE_FIELDS.map(({ label, field }) => (
									<div className="qp-print-summary-tile" key={field}>
										<div className="qp-print-summary-label">{label}</div>
										<div className="qp-print-summary-value">
											{getFieldDisplayLabel(field, formState)}
										</div>
									</div>
								))}
							</div>
						</td>
					</tr>

					{QUICK_PRICER_PRINT_SECTIONS.map((section) => {
						const fieldNames = isCorrOrNdcChannel
							? section.fieldNames.filter(
									(fieldName) => fieldName !== "lenderPaidvsBorrowerPaid"
								)
							: section.fieldNames;

						return (
							<tr key={section.title}>
								<td>
									<div className="qp-print-section">
										<div className="qp-print-section-title">{section.title}</div>
										<div className="qp-print-section-grid">
											{fieldNames.map((fieldName) => (
												<div className="qp-print-field" key={fieldName}>
													<span className="qp-print-field-label">
														{getFieldLabelText(fieldName)}
													</span>
													<span className="qp-print-field-value">
														{getFieldDisplayLabel(fieldName, formState)}
													</span>
												</div>
											))}
										</div>
									</div>
								</td>
							</tr>
						);
					})}

					<tr>
						<td>
							<div className="qp-print-section">
								<div className="qp-print-section-title">
									Selected Products &amp; Pricing
								</div>
								<div className="qp-print-section-caption">
									Showing the full rate stack for each selected product. Price
									adjustments print only for rates checked within a product
									(via Show All Options) — check one or more rates to include
									their adjustments. Up to 3 products may be selected per
									print.
								</div>
							</div>
						</td>
					</tr>

					{(selectedProducts || []).map((product) => (
						<tr key={product.productId}>
							<td>
								<PrintProgramSection
									product={product}
									selectedRateKeys={selectedRateKeys}
									isCorrOrNdcChannel={isCorrOrNdcChannel}
									lenderPaidvsBorrowerPaid={formState?.lenderPaidvsBorrowerPaid}
								/>
							</td>
						</tr>
					))}

					<tr>
						<td>
							<div className="qp-print-note">
								Each product table reflects the full rate stack at the scenario
								shown above. The price adjustments breakdown corresponds to the
								View Details summary in Quick Pricer. Rates are estimates and
								are not a lock.
							</div>
						</td>
					</tr>
				</tbody>
			</table>

			{/* Plain block, not <tfoot>: renders once after the table's last row
			    (i.e. on the last page) instead of repeating on every page. */}
			<div className="qp-print-disclaimer">
				<div className="qp-print-disclaimer-title">Disclaimer</div>
				<div>{DISCLAIMER_TEXT}</div>
			</div>
			<div className="qp-print-footer">
				<span>
					LoganConnect Quick Pricer · Generated{" "}
					{formatGeneratedTimestamp(generatedAt)}
				</span>
			</div>
			{isPublic && (
				<div className="qp-print-copyright-bar">
					<span>
						© Copyright 2012 - 2026 | Logan Finance Corporation | All Rights
						Reserved | NMLS 127722
					</span>
				</div>
			)}
		</div>
	);
};

export default PrintDocument;
